import { useMsal } from '@azure/msal-react';
import { logger } from '../../services/logger/logger';
import { useDispatch } from 'react-redux';
import { setAccessToken } from '../../store/slices/userSlice';
import Button from '../../components/common/Button';
import Stack from '@mui/material/Stack';
import { Typography, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { azureApiScope } from '../../config/msalConfig';
import { getPostLoginRedirect } from '../../utils/storage';
import { useTranslation } from 'react-i18next';

const LoginContainer = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isProcessingRedirect, setIsProcessingRedirect] = useState(true);
  const loginInProgressRef = useRef(false);
  const tokenAcquiredRef = useRef(false);

  const loginRequest = useMemo(
    () => ({
      scopes: ['openid', 'profile', 'email'],
    }),
    [],
  );

  const tokenRequest = useMemo(
    () => ({
      scopes: azureApiScope ? [azureApiScope] : [],
    }),
    [],
  );

  // GET ACCESS TOKEN
  const getAccessToken = useCallback(
    async (acct?: any) => {
      const all = instance.getAllAccounts();
      const accountToUse = acct ?? (all && all[0]) ?? (accounts && accounts[0]);

      if (!accountToUse) {
        throw new Error('No account available for token acquisition');
      }

      try {
        const response = await instance.acquireTokenSilent({
          ...tokenRequest,
          account: accountToUse,
        });

        dispatch(setAccessToken(response.accessToken));
        tokenAcquiredRef.current = true;

        // Navigate to the originally requested page (or main app) after successful token acquisition
        navigate(getPostLoginRedirect() ?? '/deliveries', { replace: true });
      } catch (error: any) {
        logger.error('Failed to acquire access token', error as Error);

        // If silent acquisition fails, try redirect (not popup)
        if (
          error?.errorCode === 'consent_required' ||
          error?.errorCode === 'interaction_required' ||
          error?.errorCode === 'login_required'
        ) {
          try {
            await instance.acquireTokenRedirect({
              ...tokenRequest,
              account: accountToUse,
            });
            // Code after acquireTokenRedirect won't execute
          } catch (e: any) {
            logger.error('Failed to acquire access token', e as Error);
            throw new Error('Failed to acquire access token');
          }
        } else {
          throw error;
        }
      }
    },
    [instance, dispatch, navigate, tokenRequest, accounts],
  );

  // Handle redirect response on component mount
  useEffect(() => {
    const handleRedirectResponse = async () => {
      try {
        setIsProcessingRedirect(true);
        const response = await instance.handleRedirectPromise();

        if (response?.account) {
          instance.setActiveAccount(response.account);
          await getAccessToken(response.account);
        } else if (accounts && accounts.length > 0 && !tokenAcquiredRef.current) {
          // User is already logged in
          instance.setActiveAccount(accounts[0]);
          await getAccessToken(accounts[0]);
        } else {
          // No authentication in progress, show login form
          setIsProcessingRedirect(false);
        }
      } catch (error: any) {
        logger.error('Failed to handle redirect', error as Error);
        setIsProcessingRedirect(false);
      }
    };

    // Only handle redirect if not already in progress
    if (inProgress === InteractionStatus.None) {
      handleRedirectResponse();
    } else {
      // If MSAL is still processing, wait
      setIsProcessingRedirect(true);
    }
  }, [instance, accounts, inProgress, getAccessToken]);

  // LOGIN - Use redirect instead of popup to avoid browser blocking
  const handleLogin = async () => {
    // Prevent multiple simultaneous login attempts
    if (loginInProgressRef.current || isLoggingIn || inProgress !== InteractionStatus.None) {
      return;
    }

    try {
      loginInProgressRef.current = true;
      setIsLoggingIn(true);

      // Use redirect flow instead of popup - much more reliable
      await instance.loginRedirect(loginRequest);

      // Note: Code after loginRedirect won't execute as the page will redirect
    } catch (e: any) {
      logger.error('Failed to login', e as Error);
      loginInProgressRef.current = false;
      setIsLoggingIn(false);
    }
  };

  // Show loading state while MSAL is processing or handling redirect
  if (
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect ||
    isProcessingRedirect
  ) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100vh" width="100%">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      height="100vh"
      width={'100%'}
      direction={'row'}
    >
      <Box
        sx={{
          width: '40%',
          height: '100vh',
          overflow: 'hidden',
          '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          },
        }}
      >
        <img src="/assets/logo/banner.svg" alt={t('common.banner', 'Banner')} />
      </Box>
      <Stack sx={{ width: '60%', justifyContent: 'center', alignItems: 'center' }}>
        <Stack sx={{ width: '55%', borderRadius: `${theme.borderRadius.lg}px`, p: 3 }} gap={'44px'}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/assets/logo/Costamare_logo.svg"
              alt={t('common.costamare', 'Costamare')}
              style={{ height: 42 }}
            />
          </Box>
          <Stack gap={0.5}>
            <Typography sx={{ fontWeight: 700, fontSize: '21px' }}>{t('login.signIn')}</Typography>
            <Typography
              sx={{
                color: theme.palette.inputField.font + '99',
                fontSize: '16px',
                fontWeight: 400,
              }}
            >
              {t('login.authDescription')}
            </Typography>
          </Stack>
          <Stack gap={2}>
            <Button
              variant="text"
              onClick={handleLogin}
              disabled={isLoggingIn || inProgress !== InteractionStatus.None}
              sx={{
                color: theme.palette.inputField.font,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                borderRadius: `${theme.borderRadius.md}px`,
                border: `1px solid ${theme.palette.divider}`,
                px: 2,
                py: 3,
                width: '100%',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                '&:hover': {
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.common.white,
                },
                '&.Mui-disabled': {
                  color: theme.palette.inputField.font + '80',
                  border: `1px solid ${theme.palette.divider + '80'}`,
                  backgroundColor: theme.palette.background.default,
                },
              }}
            >
              {isLoggingIn || inProgress !== InteractionStatus.None ? (
                <>
                  <CircularProgress size={24} sx={{ color: theme.palette.inputField.font }} />
                  {t('buttons.loggingIn')}
                </>
              ) : (
                <>
                  <img
                    src="/assets/logo/Login_Button_logo.svg"
                    alt="windows"
                    style={{ height: 30 }}
                  />
                  {t('buttons.login')}
                </>
              )}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default LoginContainer;
